/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  forwardRef,
  Fragment,
  useState,
  useMemo,
  MouseEvent as ReactMouseEvent,
  useRef,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './TenderMovementsTable.style';
import Table from 'antd/lib/table';
import { useMovementColumns } from './useMovementColumns';
import { TableRowSelection } from 'antd/lib/table/interface';
import { SelectedShareKeysProps } from '~components/molecules/Tabs/TenderTabs/TenderTabs';
import { FileTableProps } from '~components/atoms/FileTables/FileTable';
import { Button, Empty, message, Pagination, Select, Tooltip } from 'antd';
import { CorrelationLetterModal } from '~components/popups-modals/MovementsModals/CorrelationLetter.modal';
import { MovementDetailsModal } from '~components/popups-modals/MovementsModals/MovementDetails.modal';
import CherryPickPanel from '~components/atoms/CherryPickPanel/CherryPickPanel';
import {
  dbTenderMovementToTableEntry,
  TableEntry,
} from '~components/atoms/FileTables/FileTable.utils';
import dataContextMenu, {
  DataContextMenuItemType,
} from '~src/data/dataContextMenu';
import { BaseRightClickOverlay } from '~components/atoms/RightClickOverlay/BaseRightClickOverlay';
import { AddCustomMovement } from '~components/popups-modals/MovementsModals/AddCustomMovement.modal';
import { AddCustomToMovement } from '~components/popups-modals/MovementsModals/AddCustomToMovement.modal';
import { useIntl } from 'react-intl';
import ResizeObserver from 'resize-observer-polyfill';
import { debounce } from 'lodash';
import { queryCache, useMutation } from 'react-query';
import {
  manageCurrentCorrectionLetterMovements,
  updateMovementFlags,
} from '~src/services/api/movements';
import { appLog } from '~src/utils/log';

export type MovementEntry = {
  id: number | string;
  originalId?: number;
  type: number;
  created_at: number;
  created_by_user_id: number;
  created_by_user_name: string;
  target_entities: {
    type: string;
    display_name: string;
    description: string | null;
  }[];
  changes: {
    field: string;
    old_value: string;
    new_value: string;
  }[];
  custom?: {
    note: string;
    created_at: number;
    created_by_user_id: number;
    created_by_user_name: string;
  };
  summary?: {
    head: string;
    actions: string[];
  };
  report_data?: {
    is_ignore: boolean;
    is_public: boolean;
    is_report_sent: boolean;
    first_report_datetime: number | null;
    is_in_current_report: boolean;
  };
};

type TenderMovementsTableProps = Pick<
  FileTableProps,
  'highlightedRow' | 'focusOptions' | 'scroll'
> &
  SelectedShareKeysProps & {
    tenderId: string;
    projectId: number | undefined;
    movementsList: MovementEntry[];
    isLoading: boolean;
    highlightedRow?: any;
    tenderRegData: any;
    isStartFetch?: boolean;
    onSelect?: TableRowSelection<TableEntry>['onSelect'];
    onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
    setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
    selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
    isOnMyTender?: boolean;
  };

export const TenderMovementsTable = forwardRef<
  HTMLDivElement,
  TenderMovementsTableProps
>(
  (
    {
      movementsList,
      isLoading,
      tenderId,
      projectId,
      tenderRegData,
      isStartFetch,
      setSelectedItems,
      onSelect,
      onSelectAll,
      selectedKeys,
      isOnMyTender,
      ...tableProps
    },
    ref
  ) => {
    const intl = useIntl();
    const styles = useStyles(styleFn);
    const viewOnly = false;

    const [visibleCorrectionLetter, setVisibleCorrectionLetter] = useState(
      false
    );
    const [selectedMovement, setSelectedMovement] = useState<
      MovementEntry | undefined
    >(undefined);
    const [visibleMovementDetails, setVisibleMovementDetails] = useState(false);
    const [visibleAddCustom, setVisibleAddCustom] = useState(false);
    const [
      visibleAddToCustomMovement,
      setVisibleAddCustomToMovement,
    ] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Context menu state
    const [rightClickEntity, setRightClickEntity] = useState<{
      movement?: MovementEntry;
    }>({});

    const [doManageMovementsToLetter] = useMutation(
      manageCurrentCorrectionLetterMovements,
      {
        onSuccess: () => {
          // refresh movements so report_data flags (is_in_current_report / first_report_datetime) update without page refresh
          queryCache.invalidateQueries(['movements', projectId, tenderId]);

          message.success(
            intl.formatMessage({ id: 'correction-letter-movements-updated' })
          );
        },
        onError: (error, payload) => {
          message.error(
            intl.formatMessage({
              id: 'correction-letter-movements-update-failed',
            })
          );
          appLog('doManageMovementsToLetter -> payload', payload);
          appLog('doManageMovementsToLetter -> error', error);
        },
      }
    );

    const [doUpdateMovementFlags] = useMutation(updateMovementFlags, {
      onSuccess: () => {
        queryCache.invalidateQueries(['movements', projectId, tenderId]);
        message.success(
          intl.formatMessage({
            id: 'movement-flags-updated',
            defaultMessage: 'Movement updated',
          })
        );
      },
      onError: (error, payload) => {
        message.error(
          intl.formatMessage({
            id: 'movement-flags-update-failed',
            defaultMessage: 'Failed to update movement',
          })
        );
        appLog('doUpdateMovementFlags -> payload', payload);
        appLog('doUpdateMovementFlags -> error', error);
      },
    });

    // Handle context menu item clicks
    const handleRightOverlayItemClick = (item: DataContextMenuItemType) => {
      if (item.modal === 'details-log') {
        setSelectedMovement(rightClickEntity.movement);
        setVisibleMovementDetails(true);
      }
      if (item.modal === 'add-custom-to-movements') {
        setSelectedMovement(rightClickEntity.movement);
        setVisibleAddCustomToMovement(true);
      }
      if (item.modal === 'add-to-correction-letter') {
        if (!projectId) return;

        const movementIdRaw = rightClickEntity.movement?.id;
        const movementId = Number(movementIdRaw);

        if (!movementId || Number.isNaN(movementId)) return;

        doManageMovementsToLetter({
          projectId,
          tenderId: Number(tenderId),
          action: 'add_movement',
          body: {
            movement_ids: [movementId],
          },
        });

        setRightClickEntity({});
        return;
      }

      if (item.modal === 'remove-from-correction-letter') {
        if (!projectId) return;

        const movementIdRaw = rightClickEntity.movement?.id;
        const movementId = Number(movementIdRaw);

        if (!movementId || Number.isNaN(movementId)) return;

        doManageMovementsToLetter({
          projectId,
          tenderId: Number(tenderId),
          action: 'remove_movement',
          body: {
            movement_ids: [movementId],
          },
        });

        setRightClickEntity({});
        return;
      }

      if (item.modal === 'ignore-movement') {
        if (!projectId) return;

        const movementIdRaw = rightClickEntity.movement?.id;
        const movementId = Number(movementIdRaw);

        if (!movementId || Number.isNaN(movementId)) return;

        doUpdateMovementFlags({
          project_id: projectId,
          tender_id: Number(tenderId),
          movement_id: movementId,
          action: 'set_ignore_on',
        });

        setRightClickEntity({});
        return;
      }

      if (item.modal === 'reactivate-movement') {
        if (!projectId) return;

        const movementIdRaw = rightClickEntity.movement?.id;
        const movementId = Number(movementIdRaw);

        if (!movementId || Number.isNaN(movementId)) return;

        doUpdateMovementFlags({
          project_id: projectId,
          tender_id: Number(tenderId),
          movement_id: movementId,
          action: 'set_ignore_off',
        });

        setRightClickEntity({});
        return;
      }
    };

    const handleContextMenuEvent = (e: ReactMouseEvent<any>) => {
      e.preventDefault();

      const customEvent = new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        view: window,
        button: 2,
        buttons: 2,
        clientX: e.clientX,
        clientY: e.clientY,
      });

      e.currentTarget.dispatchEvent(customEvent);
    };

    const handleMovementsRow = (record: MovementEntry) => {
      return {
        onContextMenu: (e: ReactMouseEvent<any>) => {
          setRightClickEntity({ movement: record });
          handleContextMenuEvent(e);
        },
      };
    };

    const columns = useMovementColumns({
      styles,
      viewOnly,
      isStartFetch,
      onMenuDetailsClick: (e, record) => {
        setRightClickEntity({ movement: record });
        handleContextMenuEvent(e);
      },
    });

    const rowSelection: TableRowSelection<MovementEntry> = {
      selectedRowKeys: selectedKeys,
      onChange: (selectedRowKeys, selectedRows) => {
        // Map the selected movements to TableEntries for the CherryPickPanel
        const entries = selectedRows.map(dbTenderMovementToTableEntry);
        setSelectedItems(entries);
      },
      onSelect: (record, selected) => {
        if (onSelect) {
          onSelect(
            dbTenderMovementToTableEntry(record),
            selected,
            [],
            null as any
          );
        }
      },
      onSelectAll: (selected, selectedRows) => {
        // Block onSelectAll when pageSize is not equal to movementsList.length (showing all items)
        if (pageSize !== movementsList.length) return;
        if (onSelectAll) {
          const entries = selectedRows.map(dbTenderMovementToTableEntry);
          onSelectAll(selected, entries, []);
        }
      },
      getCheckboxProps: (record) => ({
        // Enable for project side, disable for vendor side (isOnMyTender)
        disabled: isOnMyTender,
        name: record.id.toString(),
      }),

      renderCell: (checked, record, index, originNode) => {
        // For header row (when record is undefined), show disabled checkbox when viewing all items
        if (!record) {
          if (pageSize !== movementsList.length) {
            // Return disabled checkbox with visual indication when showing all items
            return (
              <div
                css={{
                  opacity: 0.4,
                  cursor: 'not-allowed !important',
                  '& .ant-checkbox-input': {
                    pointerEvents: 'none',
                    cursor: 'not-allowed !important',
                  },
                  '& .ant-checkbox': {
                    cursor: 'not-allowed !important',
                  },
                  '& .ant-checkbox-inner': {
                    cursor: 'not-allowed !important',
                  },
                  '& .ant-checkbox-wrapper': {
                    cursor: 'not-allowed !important',
                  },
                  '& .ant-checkbox:hover': {
                    cursor: 'not-allowed !important',
                  },
                  '& .ant-checkbox:hover .ant-checkbox-inner': {
                    borderColor: '#d9d9d9',
                    backgroundColor: 'transparent',
                    cursor: 'not-allowed !important',
                  },
                  '& .ant-checkbox-wrapper:hover': {
                    cursor: 'not-allowed !important',
                  },
                  '& .ant-checkbox-wrapper:hover .ant-checkbox-inner': {
                    borderColor: '#d9d9d9',
                    backgroundColor: 'transparent',
                    cursor: 'not-allowed !important',
                  },
                  '& .ant-checkbox-input:focus + .ant-checkbox-inner': {
                    borderColor: '#d9d9d9',
                    boxShadow: 'none',
                    cursor: 'not-allowed !important',
                  },
                  // Ensure cursor is blocked everywhere with high specificity
                  '& *': {
                    cursor: 'not-allowed !important',
                  },
                  '& span': {
                    cursor: 'not-allowed !important',
                  },
                  '& input': {
                    cursor: 'not-allowed !important',
                  },
                }}
                title={intl.formatMessage({
                  id: 'global-checkbox-disabled-tooltip',
                })}>
                {originNode}
              </div>
            );
          } else {
            return originNode;
          }
        }
        return originNode;
      },
    };

    const overlayItems = useMemo(() => {
      const baseItems = isOnMyTender
        ? dataContextMenu.movementsActionMenuVendor
        : dataContextMenu.movementsActionMenu;

      const isInLetter =
        rightClickEntity.movement?.report_data?.is_in_current_report === true;

      const isIgnored =
        rightClickEntity.movement?.report_data?.is_ignore === true;

      return baseItems.filter((it) => {
        if (it.modal === 'add-to-correction-letter') return !isInLetter;
        if (it.modal === 'remove-from-correction-letter') return isInLetter;

        // project-side only: show only one of these actions
        if (!isOnMyTender) {
          if (it.modal === 'ignore' || it.modal === 'ignore-movement')
            return !isIgnored;
          if (it.modal === 'reactivate-movement') return isIgnored;
        }
        return true;
      });
    }, [rightClickEntity, isOnMyTender]);

    const [bottomViewHeight, setBottomViewHeight] = useState(0);
    const zoneDivRef = useRef<HTMLDivElement>(null);

    // Set up the ResizeObserver to watch the bottom panel's height
    const resizeObserverRef = useRef(
      new ResizeObserver(
        debounce(([resizeEntry]) => {
          setBottomViewHeight(resizeEntry.contentRect.height);
        }, 50)
      )
    );

    useEffect(() => {
      const div = zoneDivRef.current;
      if (div) {
        const resizeObserver = resizeObserverRef.current;
        resizeObserver.observe(div);
        return () => resizeObserver.unobserve(div);
      }
    }, []);

    // Add tooltip and blocked cursor to disabled table header checkbox
    useEffect(() => {
      if (pageSize !== movementsList.length) {
        const timer = setTimeout(() => {
          const tableHeaderCheckbox = document.querySelector(
            '.ant-table-selection-column .ant-checkbox-wrapper'
          );
          const tableHeaderCell = document.querySelector(
            '.ant-table-selection-column'
          );
          const checkboxInner = document.querySelector(
            '.ant-table-selection-column .ant-checkbox-inner'
          );
          const checkboxInput = document.querySelector(
            '.ant-table-selection-column .ant-checkbox-input'
          );
          const checkbox = document.querySelector(
            '.ant-table-selection-column .ant-checkbox'
          );

          if (tableHeaderCheckbox) {
            tableHeaderCheckbox.setAttribute(
              'title',
              intl.formatMessage({ id: 'global-checkbox-disabled-tooltip' })
            );
            // Add blocked cursor styling with high priority
            (tableHeaderCheckbox as HTMLElement).style.setProperty(
              'cursor',
              'not-allowed',
              'important'
            );
            (tableHeaderCheckbox as HTMLElement).style.opacity = '0.4';
          }

          if (tableHeaderCell) {
            (tableHeaderCell as HTMLElement).style.setProperty(
              'cursor',
              'not-allowed',
              'important'
            );
          }

          if (checkboxInner) {
            (checkboxInner as HTMLElement).style.setProperty(
              'cursor',
              'not-allowed',
              'important'
            );
          }

          if (checkboxInput) {
            (checkboxInput as HTMLElement).style.setProperty(
              'cursor',
              'not-allowed',
              'important'
            );
          }

          if (checkbox) {
            (checkbox as HTMLElement).style.setProperty(
              'cursor',
              'not-allowed',
              'important'
            );
          }
          if (checkboxInput) {
            (checkboxInput as HTMLInputElement).disabled = true;
          }
        }, 100);
        return () => clearTimeout(timer);
      } else {
        // Remove styling when enabled
        const timer = setTimeout(() => {
          const tableHeaderCheckbox = document.querySelector(
            '.ant-table-selection-column .ant-checkbox-wrapper'
          );
          const tableHeaderCell = document.querySelector(
            '.ant-table-selection-column'
          );
          const checkboxInner = document.querySelector(
            '.ant-table-selection-column .ant-checkbox-inner'
          );
          const checkboxInput = document.querySelector(
            '.ant-table-selection-column .ant-checkbox-input'
          );
          const checkbox = document.querySelector(
            '.ant-table-selection-column .ant-checkbox'
          );

          if (tableHeaderCheckbox) {
            tableHeaderCheckbox.removeAttribute('title');
            (tableHeaderCheckbox as HTMLElement).style.removeProperty('cursor');
            (tableHeaderCheckbox as HTMLElement).style.opacity = '';
          }

          if (tableHeaderCell) {
            (tableHeaderCell as HTMLElement).style.removeProperty('cursor');
          }

          if (checkboxInner) {
            (checkboxInner as HTMLElement).style.removeProperty('cursor');
          }

          if (checkboxInput) {
            (checkboxInput as HTMLElement).style.removeProperty('cursor');
            (checkboxInput as HTMLInputElement).disabled = false;
          }

          if (checkbox) {
            (checkbox as HTMLElement).style.removeProperty('cursor');
          }
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [pageSize, movementsList.length, intl]);

    // Dynamically calculate the table's scrollable height
    const parentScrollY = tableProps.scroll?.y;
    const tableScrollY = parentScrollY
      ? `calc(${parentScrollY} - ${bottomViewHeight + 60}px)` // Add extra space for bottom controls
      : undefined;

    return (
      <div
        ref={ref}
        css={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0, // Allow flex items to shrink
        }}>
        <BaseRightClickOverlay
          items={overlayItems}
          disabled={!!selectedKeys?.length}
          onItemClick={handleRightOverlayItemClick}
          onOverlayClose={() => setRightClickEntity({})}
          focusOptions={tableProps.focusOptions}>
          <div
            css={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Table<MovementEntry>
              showSorterTooltip={false}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={intl.formatMessage({ id: 'no-data' })}
                  />
                ),
              }}
              css={[
                styles.table,
                isStartFetch && styles.printTable,
                tableScrollY && {
                  '.ant-table-body': {
                    height: tableScrollY,
                  },
                },
              ]}
              className="main-content-window"
              pagination={false}
              footer={() => null}
              bordered
              columns={columns}
              dataSource={
                isStartFetch
                  ? movementsList
                  : pageSize === movementsList.length
                  ? movementsList
                  : movementsList.slice(
                      (currentPage - 1) * pageSize,
                      currentPage * pageSize
                    )
              }
              loading={isLoading}
              rowKey={(record) => String(record.id)}
              rowSelection={isStartFetch ? undefined : rowSelection}
              expandable={
                isStartFetch
                  ? {
                      expandIconColumnIndex: 3,
                      columnWidth: '2px',
                      expandIcon: () => null,
                      expandedRowRender: (record) => {
                        const isTrulyHasChanges =
                          Array.isArray(record.changes) &&
                          record.changes.length > 0;
                        const isTrulyHasTargets =
                          Array.isArray(record.target_entities) &&
                          record.target_entities.length > 0;
                        const isTrulyHasCustom = !!record.custom?.created_at;

                        if (
                          !isTrulyHasChanges &&
                          !isTrulyHasCustom &&
                          !isTrulyHasTargets
                        )
                          return null;

                        return (
                          <div
                            style={{
                              pageBreakInside: 'avoid',
                              breakInside: 'avoid',
                              padding: '8px 12px',
                              backgroundColor: '#fff',
                              borderLeft: '4px solid #e7e7e7',
                              borderBottom: '4px solid #e7e7e7',
                              borderRight: '4px solid #e7e7e7',
                              margin: '-1px 0 30px 0',
                              fontSize: '13px',
                              color: '#333',
                              lineHeight: '1.6',
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}>
                            {isTrulyHasCustom && (
                              <div css={{ width: '25%' }}>
                                <div
                                  style={{ fontWeight: 600, marginBottom: 4 }}>
                                  {intl.formatMessage({ id: 'custom-note' })}
                                </div>
                                <div>
                                  <strong>
                                    {record.custom?.created_by_user_name}:
                                  </strong>{' '}
                                  <em>{record.custom?.note || '–'}</em>
                                </div>
                              </div>
                            )}

                            {isTrulyHasChanges && (
                              <div style={{ marginBottom: 12, width: '50%' }}>
                                <div
                                  style={{ fontWeight: 600, marginBottom: 4 }}>
                                  Changes:
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                  {record.changes.map((change, i) => (
                                    <li key={`change-${i}`}>
                                      <strong>{change.field}</strong>{' '}
                                      {intl.formatMessage({
                                        id: 'changed-from',
                                      })}{' '}
                                      <em>{change.old_value || '–'}</em>{' '}
                                      {intl.formatMessage({ id: 'to' })}{' '}
                                      <em>{change.new_value || '–'}</em>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {isTrulyHasTargets && (
                              <div style={{ marginBottom: 12, width: '25%' }}>
                                <div
                                  style={{ fontWeight: 600, marginBottom: 4 }}>
                                  {intl.formatMessage({ id: 'entities' })}
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                  {record.target_entities.map((entity, i) => (
                                    <li key={`entity-${i}`}>
                                      <strong>{entity.type}</strong>:{' '}
                                      {entity.display_name}
                                      {entity.description && (
                                        <span style={{ color: '#888' }}>
                                          {' '}
                                          — {entity.description}
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      },
                      expandedRowKeys: movementsList.map((m) => String(m.id)),
                    }
                  : undefined
              }
              onRow={handleMovementsRow}
              rowClassName={(record) => {
                if (record.id === rightClickEntity.movement?.id) {
                  return 'ant-table-row-selected';
                }
                return '';
              }}
              {...tableProps}
            />
          </div>
        </BaseRightClickOverlay>

        {isStartFetch ? null : (
          <div css={[styles.underTableWrapper, styles.bottomControlsWrapper]}>
            <div
              css={{
                width: '33%',
                display: 'flex',
                gap: '10px',
                flex: 1,
              }}>
              {!isOnMyTender && (
                <Fragment>
                  <Button onClick={() => setVisibleAddCustom(true)}>
                    {intl.formatMessage({ id: 'add-custom-movement' })}
                  </Button>

                  <Button
                    onClick={() => setVisibleCorrectionLetter(true)}
                    type={'primary'}>
                    {intl.formatMessage({ id: 'movement-correction-letter' })}
                  </Button>
                </Fragment>
              )}
            </div>
            <div
              css={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 2,
                paddingLeft: isOnMyTender ? '-50px' : '70px',
              }}>
              <Pagination
                total={movementsList.length}
                current={currentPage}
                pageSize={pageSize}
                showSizeChanger={false}
                showLessItems
                showTotal={(total, range) =>
                  pageSize === movementsList.length
                    ? `${total} ${intl.formatMessage({ id: 'items' })}`
                    : `${range[0]}-${range[1]} ${intl.formatMessage({
                        id: 'of',
                      })} ${total} ${intl.formatMessage({ id: 'items' })}`
                }
                onChange={(page) => {
                  setCurrentPage(page);
                }}
                itemRender={(current, type, originalElement) => {
                  if (type === 'page' && pageSize === movementsList.length) {
                    return null; // Hide page numbers when showing all items
                  }
                  return originalElement;
                }}
              />
            </div>
            <div
              css={{
                width: '33%',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '8px',
                paddingRight: '20px',
                flex: 1,
              }}>
              <span>{intl.formatMessage({ id: 'show' })}</span>
              <Select
                value={pageSize}
                size="small"
                style={{ width: 100 }}
                onChange={(value) => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                options={[
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                  { value: 50, label: '50' },
                  {
                    value: movementsList.length,
                    label: intl.formatMessage({ id: 'all' }),
                  },
                ]}
              />
              <span>{intl.formatMessage({ id: 'per-page' })}</span>
            </div>
          </div>
        )}

        <CorrelationLetterModal
          visible={visibleCorrectionLetter}
          onCancel={() => setVisibleCorrectionLetter(false)}
          afterClose={() => setVisibleCorrectionLetter(false)}
          projectId={projectId}
          tenderId={tenderId}
          tenderRegData={tenderRegData}
        />

        <MovementDetailsModal
          visible={visibleMovementDetails}
          onCancel={() => setVisibleMovementDetails(false)}
          afterClose={() => setVisibleMovementDetails(false)}
          movement={selectedMovement}
        />

        <AddCustomMovement
          projectId={projectId}
          tenderId={tenderId}
          visible={visibleAddCustom}
          onCancel={() => setVisibleAddCustom(false)}
          afterClose={() => setVisibleAddCustom(false)}
        />

        <AddCustomToMovement
          projectId={projectId}
          tenderId={tenderId}
          movement={selectedMovement}
          visible={visibleAddToCustomMovement}
          onCancel={() => setVisibleAddCustomToMovement(false)}
          afterClose={() => setVisibleAddCustomToMovement(false)}
        />
      </div>
    );
  }
);
